import { styled } from '@mui/material/styles';
import {
  Box,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import stepConnectorClasses from '@mui/material/StepConnector/stepConnectorClasses';
import PostAttachments from 'components/modals/addPostModal/PostAttachments';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { useFormContext, useWatch } from 'react-hook-form';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import DocsImg from 'assets/images/docs.png';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.custom.pirateGold,
    borderRadius: 1,
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme }) => ({
  backgroundColor: theme.palette.custom.pirateGold,
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...theme.applyStyles('dark', {
    backgroundColor: theme.palette.custom.tuna,
  }),
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
    },
  ],
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement<unknown> } = {
    1: <Typography fontWeight={700}>1</Typography>,
    2: <Typography fontWeight={700}>2</Typography>,
    3: <Typography fontWeight={700}>3</Typography>,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

type ParseFileFormProps = {
  article: ArticleType | undefined;
  articleType?: ArticleSourceType;
  isFullScreen?: boolean;
  loadingFiles: boolean;
  response: any;
  downloadedFiles: AttachmentType[];
  onDrop: (acceptedFiles: File[]) => void;
  setDownloadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
};

function WeWillWriteForm({
  article,
  articleType,
  isFullScreen,
  loadingFiles,
  onDrop,
  response,
  downloadedFiles,
  setDownloadedFiles,
}: ParseFileFormProps) {
  const { resetField } = useFormContext<PostFormData>();
  const { control } = useFormContext<PostFormData>();
  const attachments = useWatch({
    control,
    name: 'attachments',
    defaultValue: [],
  });
  const steps = ['You Upload Files', 'We Translate Them Into Posts', 'You Approve'];
  const attachmentsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    resetField('attachments');
  }, []);

  useEffect(() => {
    if (attachmentsContainerRef.current && attachments.length) {
      const thumbnailsRef = attachmentsContainerRef.current.querySelector('#thumbnails');
      if (thumbnailsRef) {
        thumbnailsRef.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }
  }, [attachmentsContainerRef.current, attachments]);

  return (
    <Box minHeight={600} mt={2}>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.custom.earlyDawn,
          p: 2,
          borderRadius: 1,
          margin: '0 auto',
        })}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Box>
            <img src={DocsImg} width={250} />
          </Box>
          <Box>
            <Note mb={2}>
              Upload the files you've captured lessons learned and best practices in.
            </Note>
            <Note mb={2}>
              Our trained knowledge facilitators (under NDA) will extract the valuable information
              and summarize them as posts on your behalf.
            </Note>
          </Box>
        </Box>
        {/* <Stepper alternativeLabel activeStep={-1} connector={<ColorlibConnector />} sx={{ mt: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={ColorlibStepIcon}
                sx={(theme) => ({
                  '& .MuiStepLabel-label': {
                    fontWeight: 700,
                    fontSize: 14,
                  },
                })}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper> */}
      </Box>
      <Box margin="0 auto" ref={attachmentsContainerRef}>
        <PostAttachments
          isFullScreen={isFullScreen}
          articleType={articleType}
          article={article}
          onDrop={onDrop}
          loadingFiles={loadingFiles}
          dropzoneSx={(theme) => ({
            height: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px dashed ${theme.palette.custom.coolGrey}`,
          })}
        />
      </Box>
    </Box>
  );
}

const Note = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.tuna,
  fontSize: 16,
  lineHeight: 1.5,
  letterSpacing: 0.3,
}));

export default WeWillWriteForm;
